import React, { useState, useEffect, useCallback } from 'react';
import { getScheduleChangeLog, getScheduleChangeSummary, getTaskHistory } from '../api';

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */
const C = {
  slate900:'#0f172a', slate700:'#334155', slate500:'#64748b', slate400:'#94a3b8',
  slate200:'#e2e8f0', slate100:'#f1f5f9', slate50:'#f8fafc',
  blue600:'#2563eb', blue500:'#3b82f6', blue50:'#eff6ff',
  green600:'#16a34a', green50:'#f0fdf4',
  amber600:'#d97706', amber50:'#fffbeb',
  rose600:'#dc2626', rose50:'#fef2f2',
  indigo:'#6366f1', white:'#fff',
};

const fieldIcon = (field) => {
  switch (field) {
    case 'Start Date': case 'Finish Date': return '📅';
    case 'Duration': return '⏱';
    case 'Percent Complete': return '📊';
    case 'Status': return '🏷';
    case 'Responsible Party': return '👤';
    default: return '📝';
  }
};

const fieldColor = (field) => {
  switch (field) {
    case 'Start Date': return { bg: C.blue50, fg: C.blue600 };
    case 'Finish Date': return { bg: C.rose50, fg: C.rose600 };
    case 'Duration': return { bg: C.amber50, fg: C.amber600 };
    case 'Percent Complete': return { bg: C.green50, fg: C.green600 };
    case 'Status': return { bg: '#f3e8ff', fg: '#7c3aed' };
    case 'Responsible Party': return { bg: C.slate100, fg: C.slate700 };
    default: return { bg: C.slate100, fg: C.slate500 };
  }
};

const fmtDate = (d) => {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const fmtTimestamp = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
};

const isDateField = (field) => field === 'Start Date' || field === 'Finish Date';

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
const ScheduleLogTab = ({ projectId }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterField, setFilterField] = useState('');
  const [filterTask, setFilterTask] = useState('');
  const [taskHistory, setTaskHistory] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [view, setView] = useState('timeline'); // 'timeline' | 'byTask' | 'slippage'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterField) params.field = filterField;
      if (filterTask) params.task_id = filterTask;
      const [logData, summaryData] = await Promise.all([
        getScheduleChangeLog(projectId, params),
        getScheduleChangeSummary(projectId),
      ]);
      setLogs(logData);
      setSummary(summaryData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId, filterField, filterTask]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewTaskHistory = async (taskId) => {
    if (selectedTaskId === taskId) { setSelectedTaskId(null); setTaskHistory(null); return; }
    try {
      const history = await getTaskHistory(projectId, taskId);
      setTaskHistory(history);
      setSelectedTaskId(taskId);
    } catch (e) { console.error(e); }
  };

  const card = { background: C.white, borderRadius: 16, border: `1px solid ${C.slate200}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };
  const pill = (active) => ({
    padding: '8px 20px', borderRadius: 24, border: 'none',
    background: active ? C.indigo : 'transparent', color: active ? '#fff' : C.slate500,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
    boxShadow: active ? '0 2px 10px rgba(99,102,241,.3)' : 'none',
  });

  if (loading && !summary) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, border: `3px solid ${C.slate100}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: C.slate500, fontSize: 14, fontWeight: 500 }}>Loading change log...</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.slate900, letterSpacing: '-.4px' }}>Schedule Change Log</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.slate400 }}>Management view — every schedule change tracked and auditable.</p>
        </div>
        <div style={{ fontSize: 12, color: C.slate400, fontWeight: 500, padding: '6px 14px', borderRadius: 8, backgroundColor: C.slate100 }}>
          {summary?.totalChanges || 0} total changes recorded
        </div>
      </div>

      {/* Summary Cards */}
      {summary && summary.totalChanges > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {summary.byField.map(f => {
            const fc = fieldColor(f.field_changed);
            return (
              <div key={f.field_changed} onClick={() => setFilterField(filterField === f.field_changed ? '' : f.field_changed)}
                style={{
                  padding: '14px 20px', borderRadius: 12, border: `1px solid ${C.slate200}`, backgroundColor: filterField === f.field_changed ? fc.bg : C.white,
                  cursor: 'pointer', transition: 'all .15s', minWidth: 120,
                }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>
                  {fieldIcon(f.field_changed)} {f.field_changed}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: fc.fg }}>{f.change_count}</div>
                <div style={{ fontSize: 11, color: C.slate400 }}>changes</div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.slate100, borderRadius: 28, padding: 4, width: 'fit-content' }}>
        {[{ k: 'timeline', l: 'Timeline' }, { k: 'byTask', l: 'By Task' }, { k: 'slippage', l: 'Slippage Report' }].map(t => (
          <button key={t.k} style={pill(view === t.k)} onClick={() => setView(t.k)}>{t.l}</button>
        ))}
      </div>

      {/* No Data State */}
      {(!summary || summary.totalChanges === 0) && (
        <div style={{ ...card, textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>📋</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.slate500, marginBottom: 4 }}>No Schedule Changes Yet</div>
          <div style={{ fontSize: 13, color: C.slate400 }}>Changes will appear here as the PM updates task dates, durations, and progress on the schedule.</div>
        </div>
      )}

      {/* ═══ TIMELINE VIEW ═══ */}
      {view === 'timeline' && logs.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.slate900 }}>Change Timeline</h3>
            {filterField && (
              <button onClick={() => setFilterField('')} style={{ fontSize: 12, color: C.blue600, background: C.blue50, border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}>
                Clear filter: {filterField} &times;
              </button>
            )}
          </div>
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            {/* Timeline line */}
            <div style={{ position: 'absolute', left: 8, top: 4, bottom: 4, width: 2, backgroundColor: C.slate200 }} />

            {logs.map((log, idx) => {
              const fc = fieldColor(log.field_changed);
              const isDate = isDateField(log.field_changed);
              return (
                <div key={log.id} style={{ position: 'relative', paddingBottom: idx < logs.length - 1 ? 16 : 0, paddingLeft: 20 }}>
                  {/* Dot */}
                  <div style={{ position: 'absolute', left: -20, top: 4, width: 14, height: 14, borderRadius: '50%', backgroundColor: fc.bg, border: `2px solid ${fc.fg}` }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.blue600 }}>{log.task_code || '–'}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.slate900 }}>{log.task_name}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, backgroundColor: fc.bg, color: fc.fg }}>{log.field_changed}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <span style={{ color: C.slate500, textDecoration: 'line-through' }}>{isDate ? fmtDate(log.old_value) : log.old_value || '(empty)'}</span>
                        <span style={{ color: C.slate400 }}>→</span>
                        <span style={{ color: C.slate900, fontWeight: 600 }}>{isDate ? fmtDate(log.new_value) : log.new_value || '(empty)'}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.slate400, whiteSpace: 'nowrap', marginTop: 2 }}>
                      {fmtTimestamp(log.changed_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ BY TASK VIEW ═══ */}
      {view === 'byTask' && summary && summary.byTask.length > 0 && (
        <div style={card}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Changes by Task</h3>
          {summary.byTask.map(task => {
            const isExpanded = selectedTaskId === task.task_id;
            return (
              <div key={task.task_id} style={{ marginBottom: 8 }}>
                <div
                  onClick={() => handleViewTaskHistory(task.task_id)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.slate200}`,
                    backgroundColor: isExpanded ? C.blue50 : C.white, cursor: 'pointer', transition: 'all .15s',
                  }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = C.slate50; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = C.white; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.blue600 }}>{task.task_code || '–'}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.slate900 }}>{task.task_name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.indigo, backgroundColor: `${C.indigo}15`, padding: '2px 10px', borderRadius: 8 }}>
                      {task.change_count} changes
                    </span>
                    <span style={{ fontSize: 11, color: C.slate400 }}>{fmtDate(task.first_change)} – {fmtDate(task.last_change)}</span>
                    <span style={{ fontSize: 14, color: C.slate400, transition: 'transform .2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                </div>

                {/* Expanded task history */}
                {isExpanded && taskHistory && (
                  <div style={{ padding: '12px 16px 12px 32px', borderLeft: `2px solid ${C.blue500}`, marginLeft: 16, marginTop: 4 }}>
                    {taskHistory.map((h, i) => {
                      const fc = fieldColor(h.field_changed);
                      const isDate = isDateField(h.field_changed);
                      return (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: i < taskHistory.length - 1 ? `1px solid ${C.slate100}` : 'none' }}>
                          <span style={{ fontSize: 11, color: C.slate400, minWidth: 80 }}>{fmtTimestamp(h.changed_at)}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, backgroundColor: fc.bg, color: fc.fg }}>{h.field_changed}</span>
                          <span style={{ fontSize: 12, color: C.slate500, textDecoration: 'line-through' }}>{isDate ? fmtDate(h.old_value) : h.old_value || '(empty)'}</span>
                          <span style={{ fontSize: 12, color: C.slate400 }}>→</span>
                          <span style={{ fontSize: 12, color: C.slate900, fontWeight: 600 }}>{isDate ? fmtDate(h.new_value) : h.new_value || '(empty)'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ SLIPPAGE REPORT VIEW ═══ */}
      {view === 'slippage' && summary && (
        <div style={card}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Finish Date Slippage</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: C.slate400 }}>Tasks where the finish date was pushed later than the previous finish date.</p>

          {summary.slippage.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: C.slate400, fontSize: 14 }}>
              No finish date slippage recorded yet.
            </div>
          ) : (
            <div style={{ border: `1px solid ${C.slate200}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '70px 1fr 110px 110px 80px 100px',
                padding: '10px 16px', backgroundColor: C.slate50, borderBottom: `2px solid ${C.slate200}`,
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate500, gap: 8,
              }}>
                <div>Code</div><div>Task</div><div style={{ textAlign: 'center' }}>Previous Finish</div>
                <div style={{ textAlign: 'center' }}>New Finish</div><div style={{ textAlign: 'center' }}>Slipped</div>
                <div style={{ textAlign: 'right' }}>When</div>
              </div>

              {summary.slippage.map((s, idx) => {
                const oldDate = new Date(s.old_value);
                const newDate = new Date(s.new_value);
                const days = Math.round((newDate - oldDate) / (1000 * 60 * 60 * 24));
                return (
                  <div key={s.id || idx} style={{
                    display: 'grid', gridTemplateColumns: '70px 1fr 110px 110px 80px 100px',
                    padding: '10px 16px', borderBottom: idx < summary.slippage.length - 1 ? `1px solid ${C.slate100}` : 'none',
                    alignItems: 'center', gap: 8, fontSize: 13,
                    backgroundColor: days > 14 ? '#fff5f5' : 'transparent',
                  }}>
                    <div style={{ fontWeight: 600, color: C.blue600 }}>{s.task_code || '–'}</div>
                    <div style={{ color: C.slate900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.task_name}</div>
                    <div style={{ textAlign: 'center', color: C.slate500 }}>{fmtDate(s.old_value)}</div>
                    <div style={{ textAlign: 'center', color: C.rose600, fontWeight: 500 }}>{fmtDate(s.new_value)}</div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                        backgroundColor: days > 14 ? C.rose50 : C.amber50,
                        color: days > 14 ? C.rose600 : C.amber600,
                      }}>+{days}d</span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: C.slate400 }}>{fmtTimestamp(s.changed_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleLogTab;
